import { get, del, post, put } from 'aws-amplify/api';
import { APIResult, convertToFrontEnd, convertToBackEnd, getDefaultAPIHeaders } from './apiUtils';
import { Constants } from '../../utils/helpers';

const DATA_MAPPING = {
    id: "organization_id",
    name: "orgName",
    address: "address",
    contactInfo: "contactInfo",
    isActive: "isActive"
}

const DEFAULT_VALUES = {
    markedForDelete: false
}

class OrgRepository {
    constructor(defaultValues = DEFAULT_VALUES){
        this.defaultValues = defaultValues;
    }

    async getAll() {
        try {
            const raw = await getDefaultAPIHeaders();

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );   

            const getOperation = get({
                apiName: Constants.API_PATH,
                path: Constants.ORGANIZATIONS,
                options:{ headers: apiHeaders }
            });

            const response = await getOperation.response;
            const data = await response.body.json(); // Consume the response body as JSON
            const items = Array.isArray(data) ? data : data?.items ?? [];
            // Assuming 'data' is an array based on the original code's .map() call
            const retVal = items.map(i => {
                let newItem = convertToFrontEnd(DATA_MAPPING, i);

                if (this.defaultValues) {
                    newItem = {
                        ...newItem,
                        ...this.defaultValues
                    };
                }
                return newItem;
            });

            return new APIResult(false, retVal);
        } catch (err) {
            // Handle ApiError or any other potential errors
            return new APIResult(true, err);
        }
    }


    async getOne(item){
        try {
            const raw = await getDefaultAPIHeaders();

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );   

            const getOperation = get({
                apiName: Constants.API_PATH,
                path: `${Constants.ORGANIZATIONS}/${item.id}`,
                options: { headers: apiHeaders }
            });
            const response = await getOperation.response;
            const data = await response.body.json(); // Consume the response body as JSON
            //const items = Array.isArray(data) ? data : data?.items ?? [];
            let retVal = convertToFrontEnd(DATA_MAPPING, data);
            // The purpose of this spread is to preserve the values of the item
            // that are front-end only (such as whether or not they are selected)
            // and update the values that are from the back end.
            retVal = {
                ...item,
                ...retVal
            };
            return new APIResult(false, retVal);
        } catch (err) {
            // Handle ApiError or any other potential errors
            return new APIResult(true, err);
        }
    }



    // ... other imports and class context ...

    async createOne(item){
        try {
            const body = convertToBackEnd(DATA_MAPPING, item, this.defaultValues);
            const raw = await getDefaultAPIHeaders(body);

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );   
            const postOperation = post({
                apiName: Constants.API_PATH,
                path: `${Constants.ORGANIZATIONS}`,
                options: { headers: apiHeaders, body }               
            });
            
            const { body: responseBody } = await postOperation.response;
            const data = await responseBody.json();
            const items = Array.isArray(data) ? data : (data?.items ?? (data ? [data] : []));                                           
            return new APIResult(false, items);
        } catch (err) {
            return new APIResult(true, err);
        }
    }

    async updateOne(item){
        try {
            const bodyData = convertToBackEnd(DATA_MAPPING, item, this.defaultValues);
            const raw = await getDefaultAPIHeaders(bodyData);

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // // Build a pure string→string map (and drop null/undefined)
            const headers = Object.fromEntries(
            Object.entries(flat ?? {})
                 .filter(([, v]) => v != null)
                 .map(([k, v]) => [String(k), String(v)])
             ); 

            const op = put({
                apiName: Constants.API_PATH,
                path: `${Constants.ORGANIZATIONS}/${item.id}`,
                options: { headers, body: bodyData }
            });

            const { body } = await op.response;

            let data;
            if (body) {
                const text = await body.text();
                if (text && text.trim()) {
                    try { data = JSON.parse(text); } catch {}
                }
            }

            // Prefer server's updated entity; otherwise derive from what we sent
            const updated = data
            ? convertToFrontEnd(DATA_MAPPING, data)
            : { ...item, ...convertToFrontEnd(DATA_MAPPING, bodyData) };

            return new APIResult(false, updated);
        } catch (err) {
            return new APIResult(true, err);
        }
    }

    async deleteOne(item){
        try {
            const raw = await getDefaultAPIHeaders();

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );
            // Body is generally not needed for a standard DELETE, so we pass no body data to the header generator.
            const restOperation = del({
                apiName: Constants.API_PATH,
                path: `${Constants.ORGANIZATIONS}/${item.id}`,
                options: { headers: apiHeaders }
            });
            await restOperation.response;
            // We still need to wait for the response, even if we discard the body/data
            return new APIResult(false, null);            
        } catch (err) {
            return new APIResult(true, err);
        }

    }

}

export { OrgRepository };